import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Painel root: redes de clínicas (docs/api/admin.md, "Painel root: redes"; RN-ADM-54, RT-06). O
 * "grupo" do legado reduzido ao que a regra mantém: um nome, a leitura compartilhada de pacientes e
 * a composição. As rotas são globais de `root` e identificam a rede no caminho.
 */

/** A clínica dentro da rede, como a API devolve em `clinics[]`. */
export interface NetworkClinic {
  id: string
  legalName: string
  tradeName: string | null
  city: string
  state: string
  status: 'active' | 'blocked'
}

export interface Network {
  id: string
  name: string
  sharesPatients: boolean
  /** As clínicas não arquivadas da rede, em ordem de razão social. */
  clinics: NetworkClinic[]
  createdAt: string
  updatedAt: string
}

/** A rede na lista: sem `clinics[]`, com a contagem. Zero é rede vazia, a desativada. */
export interface NetworkListItem {
  id: string
  name: string
  sharesPatients: boolean
  clinicCount: number
  createdAt: string
  updatedAt: string
}

/** O corpo de criar e de editar: a composição é sempre o conjunto completo (RN-ADM-54). */
export interface NetworkInput {
  name: string
  sharesPatients: boolean
  clinicIds: string[]
}

export type NetworkFilter = 'all' | 'empty'

export interface NetworkListMeta extends ListMeta {
  counts: Record<NetworkFilter, number>
}

export const networkKeys = {
  all: ['networks'] as const,
  list: (params: object) => ['networks', 'list', params] as const,
  detail: (id: string) => ['networks', 'detail', id] as const,
  options: ['networks', 'options'] as const,
}

export function useNetworks(params: {
  filter: NetworkFilter
  search?: string
  page?: number
  perPage?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: networkKeys.list(params),
    queryFn: () =>
      api.list<NetworkListItem, NetworkListMeta>('/admin/networks', {
        filter: params.filter,
        search: params.search,
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    placeholderData: (previous) => previous,
  })
}

/**
 * Todas as redes, para o `Select` do cadastro da clínica (P-ADM-83). Uma página de 50 cobre as 16 do
 * legado com folga; passando disso, a escolha vira busca.
 */
export function useNetworkOptions(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: networkKeys.options,
    queryFn: async () => {
      const result = await api.list<NetworkListItem, NetworkListMeta>('/admin/networks', {
        filter: 'all',
        perPage: 50,
      })
      return result.data
    },
    enabled: options.enabled ?? true,
  })
}

export function useNetwork(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: networkKeys.detail(id ?? ''),
    queryFn: () => api.get<Network>(`/admin/networks/${id}`),
    enabled: Boolean(id),
  })
}

/** Toda escrita devolve a rede como ficou: o detalhe é atualizado na hora e as listas, refeitas. */
function useStoreNetwork() {
  const queryClient = useQueryClient()
  return (network: Network) => {
    queryClient.setQueryData(networkKeys.detail(network.id), network)
    // As clínicas mudaram de rede: a lista e o detalhe delas também.
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['networks', 'list'] }),
      queryClient.invalidateQueries({ queryKey: networkKeys.options }),
      queryClient.invalidateQueries({ queryKey: ['clinics'] }),
    ])
  }
}

export function useCreateNetwork() {
  const api = useApi()
  const store = useStoreNetwork()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: NetworkInput) =>
      api.post<Network>('/admin/networks', input, { idempotencyKey: idempotency.keyFor(input) }),
    onSuccess: (created) => {
      idempotency.reset()
      return store(created)
    },
  })
}

export function useUpdateNetwork(id: string) {
  const api = useApi()
  const store = useStoreNetwork()
  return useMutation({
    mutationFn: (input: NetworkInput) => api.put<Network>(`/admin/networks/${id}`, input),
    onSuccess: store,
  })
}
